import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  LinearProgress,
  Alert,
  Tooltip,
  Card,
  CardContent,
  Stack,
  Avatar,
  Grid,
  Container,
  Badge,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  DirectionsCar as CarIcon,
  LocalParking as ParkingIcon,
  Build as MaintenanceIcon,
  PhotoCamera as PhotoCameraIcon,
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  LocationOn as LocationIcon,
  Settings as SettingsIcon,
  CalendarToday as CalendarIcon,
  ZoomIn as ZoomInIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  TrendingUp as TrendingUpIcon,
  MoreVert as MoreVertIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../store';
import { fetchVehicles, createVehicle, updateVehicle, deleteVehicle } from '../store/vehiclesSlice';
import { selectVehiclesList, selectVehiclesLoading, selectVehiclesError } from '../store/selectors';
import { FRENCH_LABELS } from '../config/labels';
import { api, vehicleTypeService, vehicleCategoryService, vehicleSettingsService, vehicleNotesService, maintenanceSettingsService, vehicleConditionAssessmentService } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import type { Vehicle, VehicleFormData, ParkingSpot, MaintenanceRecord, VehicleType, VehicleCategory, VehicleConditionAssessment } from '../types';
import CurrentLayoutDisplay from '../components/CurrentLayoutDisplay';
import { getDropdownMenuProps } from '../utils/dropdownProps';
import { SYSTEM_MAINTENANCE_TYPES } from '../constants/maintenanceTypes';

const STATUS_OPTIONS = [
  { value: 'ready', label: 'Prêt', color: '#4caf50' },
  { value: 'under_maintenance', label: 'En maintenance', color: '#ff9800' },
  { value: 'washing', label: 'Lavage', color: '#9c27b0' },
  { value: 'out', label: 'Sorti', color: '#607d8b' },
  { value: 'returned', label: 'Retourné', color: '#795548' },
];

const FUEL_TYPES = ['Essence', 'Diesel', 'Électrique', 'Hybride'];
const TRANSMISSION_TYPES = ['Manuelle', 'Automatique'];

// Utility function to format date for HTML date input
const formatDateForInput = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  } catch {
    return '';
  }
};

const VehicleForm: React.FC<{
  open: boolean;
  vehicle?: Vehicle | null;
  onClose: () => void;
  onSubmit: (data: VehicleFormData, maintenanceRecords: any[], originalMaintenanceRecords: any[]) => void;
  loading: boolean;
  onShowParkingLocation?: (vehicle: Vehicle) => void;
}> = ({ open, vehicle, onClose, onSubmit, loading, onShowParkingLocation }) => {
  const [formData, setFormData] = useState<VehicleFormData>({
    registration_number: '',
    marque: '',
    modele: '',
    year: new Date().getFullYear(),
    mileage: 0,
    status: 'ready',
    service_interval_km: 15000,
    insurance_auto_renewal: false,
    // Initialize image fields
    primary_image: undefined,
    additional_images: undefined,
    remove_primary_image: false,
    remove_additional_images: [],
  });
  
  const [primaryImagePreview, setPrimaryImagePreview] = useState<string | null>(null);
  const [additionalImagePreviews, setAdditionalImagePreviews] = useState<string[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
  const [vehicleCategories, setVehicleCategories] = useState<VehicleCategory[]>([]);
  const [maintenanceTypes, setMaintenanceTypes] = useState<any[]>([]);
  
  // Notes state for the notes section
  const [notes, setNotes] = useState<any[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [maintenanceRecords, setMaintenanceRecords] = useState<any[]>([]);
  const [originalMaintenanceRecords, setOriginalMaintenanceRecords] = useState<any[]>([]);
  const [showAddMaintenanceForm, setShowAddMaintenanceForm] = useState(false);
  const [newMaintenanceRecord, setNewMaintenanceRecord] = useState({
    maintenance_type_id: undefined as number | undefined,
    maintenance_type: '',
    date_revision: '',
    prochaine_revision: '',
    start_date: format(new Date(), 'yyyy-MM-dd'),
    completion_date: ''
  });

  // Load vehicle types, categories, and maintenance types
  useEffect(() => {
    const loadVehicleTypesAndCategories = async () => {
      try {
        const [settingsResponse, maintenanceResponse] = await Promise.all([
          vehicleSettingsService.getSettings(),
          maintenanceSettingsService.getSettings()
        ]);
        
        const settings = settingsResponse.data.data;
        const maintenanceSettings = maintenanceResponse.data.data;
        
        // Use client-specific types/categories only
        setVehicleTypes(settings.types || []);
        setVehicleCategories(settings.categories || []);
        setMaintenanceTypes(maintenanceSettings.types || []);
      } catch (error) {
        console.error('Error loading vehicle types, categories, and maintenance types:', error);
      }
    };

    if (open) {
      loadVehicleTypesAndCategories();
      if (vehicle) {
        loadNotes();
        loadMaintenanceRecords();
      } else {
        // Clear maintenance records when creating new vehicle
        setMaintenanceRecords([]);
      }
    }
  }, [open, vehicle]);

  const loadMaintenanceRecords = async () => {
    if (!vehicle) return;
    
    try {
      const response = await api.get(`/vehicles/${vehicle.id}/maintenance`);
      const maintenanceData = response.data.data || [];
      
      // Transform the maintenance records to match our form structure
      const transformedRecords = maintenanceData.map((record: any) => ({
        id: record.id,
        maintenance_type_id: record.maintenance_type_id,
        maintenance_type: record.maintenance_type,
        start_date: record.start_date,
        completion_date: record.completion_date,
        description: record.description || '',
        cost: record.cost || 0,
        service_provider: record.service_provider || '',
        notes: record.notes || '',
        status: record.status || 'scheduled',
        is_system_generated: record.is_system_generated || false,
        alert_type: record.alert_type || null
      }));
      
      // Update the parent component's state
      setMaintenanceRecords(transformedRecords);
      setOriginalMaintenanceRecords(transformedRecords);
    } catch (error) {
      console.error('Error loading maintenance records:', error);
      setMaintenanceRecords([]);
      setOriginalMaintenanceRecords([]);
    }
  };

  const loadNotes = async () => {
    if (!vehicle) return;
    
    setLoadingNotes(true);
    try {
      const response = await vehicleNotesService.getNotes(vehicle.id);
      setNotes(response.data.data || []);
    } catch (error) {
      console.error('Error loading notes:', error);
      setNotes([]);
    } finally {
      setLoadingNotes(false);
    }
  };

  const handleAddNote = async () => {
    if (!vehicle || !newNote.trim()) return;
    
    setAddingNote(true);
    try {
      const response = await vehicleNotesService.createNote(vehicle.id, newNote.trim());
      setNotes(prev => [response.data.data, ...prev]);
      setNewNote('');
    } catch (error) {
      console.error('Error adding note:', error);
    } finally {
      setAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId: number) => {
    if (!vehicle) return;
    
    try {
      await vehicleNotesService.deleteNote(vehicle.id, noteId);
      setNotes(prev => prev.filter(note => note.id !== noteId));
    } catch (error) {
      console.error('Error deleting note:', error);
    }
  };

  // Reset form when vehicle changes (not when dialog opens)
  useEffect(() => {
      if (vehicle) {
        setFormData({
          registration_number: vehicle.registration_number,
          marque: vehicle.marque,
          modele: vehicle.modele,
          year: vehicle.year,
          mileage: vehicle.mileage,
          date_achat: formatDateForInput(vehicle.date_achat),
          status: vehicle.status,
          equipe_assignee: vehicle.equipe_assignee,
          fuel_type: vehicle.fuel_type,
          transmission_type: vehicle.transmission_type,
          color: vehicle.color,
          nb_portes: vehicle.nb_portes,
          nb_places: vehicle.nb_places,
          insurance_expiry: formatDateForInput(vehicle.insurance_expiry),
          insurance_auto_renewal: vehicle.insurance_auto_renewal || false,
          first_registration_date: formatDateForInput(vehicle.first_registration_date),
          service_interval_km: vehicle.service_interval_km,
        vehicle_type_id: vehicle.vehicle_type_id,
        vehicle_category_id: vehicle.vehicle_category_id,
          // Initialize image fields as undefined (will be set when files are selected)
          primary_image: undefined,
          additional_images: undefined,
          remove_primary_image: false,
          remove_additional_images: [],
        });
        // Set existing image previews
        setPrimaryImagePreview(vehicle.primary_image_url || null);
        setAdditionalImagePreviews([]);
    } else if (open) {
      // Only reset for new vehicle when dialog is open
        setFormData({
          registration_number: '',
          marque: '',
          modele: '',
          year: new Date().getFullYear(),
          mileage: 0,
          status: 'ready',
          service_interval_km: 15000,
          insurance_auto_renewal: false,
        vehicle_type_id: undefined,
        vehicle_category_id: undefined,
          // Initialize image fields for new vehicle
          primary_image: undefined,
          additional_images: undefined,
          remove_primary_image: false,
          remove_additional_images: [],
        });
        // Clear image previews for new vehicle
        setPrimaryImagePreview(null);
        setAdditionalImagePreviews([]);
      }
  }, [vehicle]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData, maintenanceRecords, originalMaintenanceRecords);
  };

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handlePrimaryImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type and size
      if (!file.type.startsWith('image/')) {
        alert('Veuillez sélectionner un fichier image valide.');
        return;
      }
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        alert('La taille du fichier ne doit pas dépasser 2MB.');
        return;
      }
      
      setFormData(prev => ({ ...prev, primary_image: file }));
      const reader = new FileReader();
      reader.onload = () => setPrimaryImagePreview(reader.result as string);
      reader.readAsDataURL(file);
    }
    // Reset input value to allow selecting the same file again
    event.target.value = '';
  };
  
  const handleAdditionalImagesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      // Validate files
      const validFiles = files.filter(file => {
        if (!file.type.startsWith('image/')) {
          alert(`Le fichier "${file.name}" n'est pas une image valide.`);
          return false;
        }
        if (file.size > 2 * 1024 * 1024) {
          alert(`Le fichier "${file.name}" est trop volumineux (max 2MB).`);
          return false;
        }
        return true;
      });
      
      if (validFiles.length > 0) {
        setFormData(prev => ({ 
          ...prev, 
          additional_images: [...(prev.additional_images || []), ...validFiles] 
        }));
        
        validFiles.forEach(file => {
          const reader = new FileReader();
          reader.onload = () => {
            setAdditionalImagePreviews(prev => [...prev, reader.result as string]);
          };
          reader.readAsDataURL(file);
        });
      }
    }
    // Reset input value to allow selecting the same files again
    event.target.value = '';
  };
  
  const removePrimaryImage = () => {
    console.log('removePrimaryImage called, current formData:', formData);
    setFormData(prev => {
      const newData = { 
        ...prev, 
        primary_image: undefined, 
        remove_primary_image: true 
      };
      console.log('Setting new formData:', newData);
      return newData;
    });
    // Clear preview safely
    setTimeout(() => {
    setPrimaryImagePreview(null);
    }, 0);
  };
  
  const removeAdditionalImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      additional_images: prev.additional_images?.filter((_, i) => i !== index)
    }));
    // Clear preview safely
    setTimeout(() => {
    setAdditionalImagePreviews(prev => prev.filter((_, i) => i !== index));
    }, 0);
  };

  const removeExistingAdditionalImage = (index: number) => {
    const filename = vehicle?.additional_images?.[index];
    if (filename) {
      setFormData(prev => ({
        ...prev,
        remove_additional_images: [
          ...(prev.remove_additional_images || []),
          filename
        ]
      }));
    }
  };

  // Calculate total additional images count
  const totalAdditionalImages = (formData.additional_images?.length || 0) + 
    (vehicle?.additional_images_urls?.length || 0) - 
    (formData.remove_additional_images?.length || 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {vehicle ? 'Modifier le véhicule' : 'Ajouter un véhicule'}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label={FRENCH_LABELS.registrationNumber}
                value={formData.registration_number}
                onChange={(e) => handleChange('registration_number', e.target.value)}
                required
              />
              <FormControl fullWidth>
                <InputLabel>{FRENCH_LABELS.status}</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        zIndex: 9999,
                      },
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                  }}
                >
                  {STATUS_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label={FRENCH_LABELS.marque}
                value={formData.marque}
                onChange={(e) => handleChange('marque', e.target.value)}
                required
              />
              <TextField
                fullWidth
                label={FRENCH_LABELS.modele}
                value={formData.modele}
                onChange={(e) => handleChange('modele', e.target.value)}
                required
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                type="number"
                label={FRENCH_LABELS.year}
                value={formData.year}
                onChange={(e) => handleChange('year', parseInt(e.target.value))}
                required
                inputProps={{ min: 1990, max: new Date().getFullYear() + 1 }}
              />
              <TextField
                fullWidth
                type="number"
                label="Kilométrage"
                value={formData.mileage}
                onChange={(e) => handleChange('mileage', parseInt(e.target.value))}
                required
                inputProps={{ min: 0 }}
              />
              <TextField
                fullWidth
                type="number"
                label={FRENCH_LABELS.serviceInterval}
                value={formData.service_interval_km}
                onChange={(e) => handleChange('service_interval_km', parseInt(e.target.value))}
                inputProps={{ min: 0, step: 1000 }}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                type="date"
                label={FRENCH_LABELS.dateAchat}
                value={formData.date_achat || ''}
                onChange={(e) => handleChange('date_achat', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label={FRENCH_LABELS.equipe}
                value={formData.equipe_assignee || ''}
                onChange={(e) => handleChange('equipe_assignee', e.target.value)}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Type de véhicule</InputLabel>
                <Select
                  value={formData.vehicle_type_id && vehicleTypes.find(type => type.id === formData.vehicle_type_id) ? formData.vehicle_type_id.toString() : ''}
                  onChange={(e) => handleChange('vehicle_type_id', e.target.value ? parseInt(e.target.value) : undefined)}
                  label="Type de véhicule"
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        zIndex: 9999,
                      },
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>Aucun</em>
                  </MenuItem>
                  {vehicleTypes.map(type => (
                    <MenuItem key={type.id} value={type.id.toString()}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Catégorie de véhicule</InputLabel>
                <Select
                  value={formData.vehicle_category_id && vehicleCategories.find(cat => cat.id === formData.vehicle_category_id) ? formData.vehicle_category_id.toString() : ''}
                  onChange={(e) => handleChange('vehicle_category_id', e.target.value ? parseInt(e.target.value) : undefined)}
                  label="Catégorie de véhicule"
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        zIndex: 9999,
                      },
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>Aucune</em>
                  </MenuItem>
                  {vehicleCategories.map(category => (
                    <MenuItem key={category.id} value={category.id.toString()}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel>{FRENCH_LABELS.fuelType}</InputLabel>
                <Select
                  value={formData.fuel_type || ''}
                  onChange={(e) => handleChange('fuel_type', e.target.value)}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        zIndex: 9999,
                      },
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                  }}
                >
                  {FUEL_TYPES.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>{FRENCH_LABELS.transmissionType}</InputLabel>
                <Select
                  value={formData.transmission_type || ''}
                  onChange={(e) => handleChange('transmission_type', e.target.value)}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        zIndex: 9999,
                      },
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                  }}
                >
                  {TRANSMISSION_TYPES.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label={FRENCH_LABELS.color}
                value={formData.color || ''}
                onChange={(e) => handleChange('color', e.target.value)}
              />
              <TextField
                fullWidth
                type="number"
                label="Nombre de portes"
                value={formData.nb_portes ?? ''}
                onChange={(e) => handleChange('nb_portes', e.target.value ? parseInt(e.target.value, 10) : undefined)}
                inputProps={{ min: 2, max: 6 }}
              />
              <TextField
                fullWidth
                type="number"
                label="Nombre de places"
                value={formData.nb_places ?? ''}
                onChange={(e) => handleChange('nb_places', e.target.value ? parseInt(e.target.value, 10) : undefined)}
                inputProps={{ min: 1, max: 20 }}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                type="date"
                label={FRENCH_LABELS.insuranceExpiry}
                value={formData.insurance_expiry || ''}
                onChange={(e) => handleChange('insurance_expiry', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                type="date"
                label={FRENCH_LABELS.registrationExpiry}
                value={formData.first_registration_date || ''}
                onChange={(e) => handleChange('first_registration_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>

            {/* Insurance Auto-Renewal Checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.insurance_auto_renewal || false}
                  onChange={(e) => handleChange('insurance_auto_renewal', e.target.checked)}
                  color="primary"
                />
              }
              label="Renouvellement automatique de l'assurance"
              sx={{ mt: 1 }}
            />

            {/* Maintenance Section */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2 }}>
                  Maintenance
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setShowAddMaintenanceForm(true)}
                  size="small"
                >
                  Ajouter maintenance
                </Button>
              </Box>


              {/* List of Maintenance Records */}
              {maintenanceRecords.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Enregistrements de maintenance
                  </Typography>
                  <List>
                    {maintenanceRecords.map((record, index) => (
                      <ListItem key={record.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box component="span" sx={{ fontWeight: 600 }}>
                                {record.maintenance_type || 'Type non défini'}
                              </Box>
                              <Chip 
                                size="small" 
                                label={record.status || 'scheduled'} 
                                color={record.status === 'completed' ? 'success' : 'default'}
                              />
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                                Début: {new Date(record.start_date).toLocaleDateString('fr-FR')}
                              </Box>
                              {record.completion_date && (
                                <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.875rem', display: 'block' }}>
                                  Terminé: {new Date(record.completion_date).toLocaleDateString('fr-FR')}
                                </Box>
                              )}
                              {record.description && (
                                <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.875rem', display: 'block', mt: 0.5 }}>
                                  {record.description}
                                </Box>
                              )}
                            </Box>
                          }
                          primaryTypographyProps={{ component: 'div' }}
                          secondaryTypographyProps={{ component: 'div' }}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={() => setMaintenanceRecords(maintenanceRecords.filter((_: any, i: number) => i !== index))}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Add New Maintenance Form */}
              {showAddMaintenanceForm && (
                <Card sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'primary.main' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1">
                      Nouvelle maintenance
                    </Typography>
                    <IconButton
                      onClick={() => setShowAddMaintenanceForm(false)}
                      size="small"
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>

                  <Stack spacing={2}>
                    <FormControl fullWidth>
                      <InputLabel>Type de maintenance</InputLabel>
                      <Select
                        value={newMaintenanceRecord.maintenance_type_id ? newMaintenanceRecord.maintenance_type_id.toString() : newMaintenanceRecord.maintenance_type || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Check if it's a system type (string) or custom type (number)
                          const systemType = SYSTEM_MAINTENANCE_TYPES.find(type => type.name === value);
                          if (systemType) {
                            // Auto-calculate completion date for system types
                            const startDate = new Date(newMaintenanceRecord.start_date);
                            const completionDate = new Date(startDate);
                            completionDate.setDate(completionDate.getDate() + systemType.duration_days);
                            
                            setNewMaintenanceRecord({
                              ...newMaintenanceRecord,
                              maintenance_type_id: undefined,
                              maintenance_type: systemType.name,
                              completion_date: completionDate.toISOString().split('T')[0]
                            });
                          } else {
                            const typeId = parseInt(value);
                            const selectedType = maintenanceTypes.find(type => type.id === typeId);
                            
                            // Auto-calculate completion date for custom types
                            if (selectedType?.duration_days) {
                              const startDate = new Date(newMaintenanceRecord.start_date);
                              const completionDate = new Date(startDate);
                              completionDate.setDate(completionDate.getDate() + selectedType.duration_days);
                              
                              setNewMaintenanceRecord({
                                ...newMaintenanceRecord,
                                maintenance_type_id: typeId,
                                maintenance_type: selectedType?.name || '',
                                completion_date: completionDate.toISOString().split('T')[0]
                              });
                            } else {
                              setNewMaintenanceRecord({
                                ...newMaintenanceRecord,
                                maintenance_type_id: typeId,
                                maintenance_type: selectedType?.name || ''
                              });
                            }
                          }
                        }}
                        label="Type de maintenance"
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 300,
                              zIndex: 9999,
                            },
                          },
                        }}
                      >
                        {SYSTEM_MAINTENANCE_TYPES.map(type => (
                          <MenuItem 
                            key={type.id} 
                            value={type.name}
                            sx={{ 
                              fontWeight: 600,
                              color: 'primary.main',
                              borderLeft: '3px solid',
                              borderLeftColor: 'primary.main',
                              pl: 1.5,
                              fontStyle: 'italic',
                              opacity: 0.9,
                              '&:hover': {
                                backgroundColor: 'primary.lighter',
                              }
                            }}
                          >
                            {type.name}{type.duration_days > 0 ? ` (${type.duration_days} ${type.duration_days === 1 ? 'jour' : 'jours'})` : ''}
                          </MenuItem>
                        ))}
                        {maintenanceTypes.map(type => (
                          <MenuItem key={type.id} value={type.id.toString()}>
                            {type.name} {type.duration_days && `(${type.duration_days} jours)`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {newMaintenanceRecord.maintenance_type_id && (
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <TextField
                          fullWidth
                          type="date"
                          label="Date de dernière révision"
                          value={newMaintenanceRecord.date_revision}
                          onChange={(e) => {
                            setNewMaintenanceRecord({ ...newMaintenanceRecord, date_revision: e.target.value });
                            
                            // Auto-calculate next revision date
                            if (e.target.value && newMaintenanceRecord.maintenance_type_id) {
                              const selectedType = maintenanceTypes.find(type => type.id === newMaintenanceRecord.maintenance_type_id);
                              if (selectedType && selectedType.duration_days) {
                                const lastRevisionDate = new Date(e.target.value);
                                const nextRevisionDate = new Date(lastRevisionDate);
                                nextRevisionDate.setDate(nextRevisionDate.getDate() + selectedType.duration_days);
                                setNewMaintenanceRecord({
                                  ...newMaintenanceRecord,
                                  date_revision: e.target.value,
                                  prochaine_revision: nextRevisionDate.toISOString().split('T')[0]
                                });
                              }
                            }
                          }}
                          InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                          fullWidth
                          type="date"
                          label="Prochaine révision (calculée automatiquement)"
                          value={newMaintenanceRecord.prochaine_revision}
                          onChange={(e) => {
                            setNewMaintenanceRecord({ ...newMaintenanceRecord, prochaine_revision: e.target.value });
                          }}
                          InputLabelProps={{ shrink: true }}
                          helperText="Calculée automatiquement selon le type de maintenance"
                        />
                      </Stack>
                    )}

                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        onClick={() => setShowAddMaintenanceForm(false)}
                      >
                        Annuler
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => {
                          if (newMaintenanceRecord.maintenance_type && newMaintenanceRecord.date_revision) {
                            setMaintenanceRecords([...maintenanceRecords, {
                              ...newMaintenanceRecord,
                              id: `temp_${Date.now()}`
                            }]);
                            setNewMaintenanceRecord({
                              maintenance_type_id: undefined,
                              maintenance_type: '',
                              date_revision: '',
                              prochaine_revision: '',
                              start_date: format(new Date(), 'yyyy-MM-dd'),
                              completion_date: ''
                            });
                            setShowAddMaintenanceForm(false);
                          }
                        }}
                      >
                        Ajouter
                      </Button>
                    </Box>
                  </Stack>
                </Card>
              )}

              {maintenanceRecords.length === 0 && !showAddMaintenanceForm && (
                <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  <Typography variant="body2">
                    Aucun enregistrement de maintenance ajouté
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                    Cliquez sur "Ajouter maintenance" pour créer des enregistrements
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Image Upload Section */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Images du véhicule
              </Typography>
              
              {/* Primary Image */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Image principale
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Cette image sera affichée en premier pour identifier le véhicule
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="primary-image-upload"
                    type="file"
                    onChange={handlePrimaryImageChange}
                  />
                  <label htmlFor="primary-image-upload">
                    <Button
                      variant="contained"
                      component="span"
                      startIcon={<PhotoCameraIcon />}
                      sx={{
                        backgroundColor: '#1976d2',
                        '&:hover': {
                          backgroundColor: '#1565c0',
                        }
                      }}
                    >
                      Choisir une image
                    </Button>
                  </label>
                  {(primaryImagePreview || vehicle?.primary_image_url) && (
                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                      <Avatar
                        src={primaryImagePreview || vehicle?.primary_image_url}
                        sx={{ width: 80, height: 80 }}
                        variant="rounded"
                      />
                      <IconButton
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          bgcolor: 'error.main',
                          color: 'white',
                          '&:hover': { bgcolor: 'error.dark' }
                        }}
                        onClick={removePrimaryImage}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Stack>
              </Box>
              
              {/* Additional Images */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Images supplémentaires
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Ajoutez des photos supplémentaires du véhicule (max 2MB par image)
                </Typography>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="additional-images-upload"
                    type="file"
                    multiple
                    onChange={handleAdditionalImagesChange}
                  />
                  <label htmlFor="additional-images-upload">
                    <Button
                      variant="contained"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                      sx={{
                        backgroundColor: '#1976d2',
                        '&:hover': {
                          backgroundColor: '#1565c0',
                        }
                      }}
                    >
                      Ajouter des images
                    </Button>
                  </label>
                  
                  {/* Image counter */}
                  <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                    {totalAdditionalImages} image{totalAdditionalImages !== 1 ? 's' : ''}
                  </Typography>
                </Stack>
                
                {/* Image previews */}
                {(additionalImagePreviews.length > 0 || vehicle?.additional_images_urls?.length) && (
                  <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1, maxWidth: 600 }}>
                    {/* Existing images from vehicle */}
                    {vehicle?.additional_images_urls?.map((url, index) => {
                      // Check if this image is marked for removal
                      const isMarkedForRemoval = formData.remove_additional_images?.includes(
                        vehicle.additional_images?.[index] || ''
                      );
                      
                      if (isMarkedForRemoval) {
                        return <React.Fragment key={`existing-${index}`}></React.Fragment>;
                      }
                      
                      return (
                        <Box key={`existing-${index}`} sx={{ position: 'relative', display: 'inline-block' }}>
                          <Avatar
                            src={url}
                            sx={{ width: 60, height: 60 }}
                            variant="rounded"
                          />
                          <IconButton
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: -4,
                              right: -4,
                              bgcolor: 'error.main',
                              color: 'white',
                              '&:hover': { bgcolor: 'error.dark' }
                            }}
                            onClick={() => removeExistingAdditionalImage(index)}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      );
                    })}
                    
                    {/* New image previews */}
                    {additionalImagePreviews.map((preview, index) => (
                      <Box key={`preview-${index}`} sx={{ position: 'relative', display: 'inline-block' }}>
                        <Avatar
                          src={preview}
                          sx={{ width: 60, height: 60 }}
                          variant="rounded"
                        />
                        <IconButton
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: -4,
                            right: -4,
                            bgcolor: 'error.main',
                            color: 'white',
                            '&:hover': { bgcolor: 'error.dark' }
                          }}
                          onClick={() => removeAdditionalImage(index)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>

            {/* Notes Section */}
            <Card>
              <CardContent>
                <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
                  Notes
                </Typography>
                
                {/* Add Note Form */}
                <Box sx={{ mb: 3 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    placeholder="Ajouter une note..."
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    disabled={addingNote}
                  />
                  <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      onClick={handleAddNote}
                      disabled={!newNote.trim() || addingNote}
                      size="small"
                    >
                      {addingNote ? <CircularProgress size={16} /> : 'Ajouter'}
                    </Button>
                  </Box>
                </Box>

                {/* Notes List */}
                {loadingNotes ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : notes.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    Aucune note pour ce véhicule
                  </Typography>
                ) : (
                  <Stack spacing={2}>
                    {notes.map((note) => (
                      <Box
                        key={note.id}
                        sx={{
                          p: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          backgroundColor: 'background.paper',
                          position: 'relative'
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Typography variant="body1" sx={{ flex: 1, mr: 2 }}>
                            {note.content}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteNote(note.id)}
                            sx={{ 
                              color: 'error.main',
                              '&:hover': { backgroundColor: 'error.50' }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(note.created_at).toLocaleString('fr-FR')}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            {FRENCH_LABELS.cancel}
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : FRENCH_LABELS.save}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const VehicleDetail: React.FC<{
  open: boolean;
  vehicle: Vehicle | null;
  onClose: () => void;
  onShowParkingLocation?: (vehicle: Vehicle) => void;
  onShowImageGallery?: (vehicle: Vehicle) => void;
}> = ({ open, vehicle, onClose, onShowParkingLocation, onShowImageGallery }) => {
  const [maintenanceRecords, setMaintenanceRecords] = useState<MaintenanceRecord[]>([]);
  const [loadingMaintenance, setLoadingMaintenance] = useState(false);
  const [notes, setNotes] = useState<any[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [assessments, setAssessments] = useState<VehicleConditionAssessment[]>([]);
  const [loadingAssessments, setLoadingAssessments] = useState(false);

  useEffect(() => {
    if (open && vehicle) {
      loadMaintenanceRecords();
      loadNotes();
      loadAssessments();
    }
  }, [open, vehicle]);

  const loadMaintenanceRecords = async () => {
    if (!vehicle) return;
    
    setLoadingMaintenance(true);
    try {
      const response = await api.get(`/vehicles/${vehicle.id}/maintenance`);
      setMaintenanceRecords(response.data.data || []);
    } catch (error) {
      console.error('Error loading maintenance records:', error);
      setMaintenanceRecords([]);
    } finally {
      setLoadingMaintenance(false);
    }
  };

  const loadNotes = async () => {
    if (!vehicle) return;
    
    setLoadingNotes(true);
    try {
      const response = await vehicleNotesService.getNotes(vehicle.id);
      setNotes(response.data.data || []);
    } catch (error) {
      console.error('Error loading notes:', error);
      setNotes([]);
    } finally {
      setLoadingNotes(false);
    }
  };

  const loadAssessments = async () => {
    if (!vehicle) return;
    
    setLoadingAssessments(true);
    try {
      const response = await vehicleConditionAssessmentService.getAssessments(vehicle.id);
      setAssessments(response.data.data || []);
    } catch (error) {
      console.error('Error loading assessments:', error);
      setAssessments([]);
    } finally {
      setLoadingAssessments(false);
    }
  };

  if (!vehicle) return null;

  const statusOption = STATUS_OPTIONS.find(s => s.value === vehicle.status);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Détails du véhicule - {vehicle.registration_number}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          {/* Vehicle Images Section */}
          {(vehicle.primary_image || (vehicle.additional_images && vehicle.additional_images.length > 0)) && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  <PhotoCameraIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Images du véhicule
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Grid container spacing={2}>
                    {/* Primary Image */}
                    {vehicle.primary_image && (
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Box
                          sx={{
                            position: 'relative',
                            width: '100%',
                            height: 300,
                            borderRadius: 2,
                            overflow: 'hidden',
                            border: '2px solid',
                            borderColor: 'primary.main',
                            cursor: 'pointer',
                            '&:hover': {
                              transform: 'scale(1.02)',
                              transition: 'transform 0.2s ease-in-out',
                            }
                          }}
                          onClick={() => onShowImageGallery?.(vehicle)}
                        >
                          <img
                            src={vehicle.primary_image_url}
                            alt={`${vehicle.marque} ${vehicle.modele} - Image principale`}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 8,
                              left: 8,
                              backgroundColor: 'primary.main',
                              color: 'white',
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              fontSize: '0.75rem',
                              fontWeight: 'bold'
                            }}
                          >
                            Image principale
                          </Box>
                        </Box>
                      </Grid>
                    )}
                    
                    {/* Additional Images */}
                    {vehicle.additional_images && vehicle.additional_images.length > 0 && (
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Grid container spacing={1}>
                          {vehicle.additional_images.slice(0, 4).map((image, index) => (
                            <Grid size={6} key={index}>
                              <Box
                                sx={{
                                  position: 'relative',
                                  width: '100%',
                                  height: 140,
                                  borderRadius: 1,
                                  overflow: 'hidden',
                                  border: '1px solid',
                                  borderColor: 'grey.300',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    transform: 'scale(1.05)',
                                    transition: 'transform 0.2s ease-in-out',
                                    borderColor: 'primary.main',
                                  }
                                }}
                                onClick={() => onShowImageGallery?.(vehicle)}
                              >
                                <img
                                  src={vehicle.additional_images_urls?.[index]}
                                  alt={`${vehicle.marque} ${vehicle.modele} - Image ${index + 1}`}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                  }}
                                />
                                {index === 3 && vehicle.additional_images && vehicle.additional_images.length > 4 && (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      bottom: 0,
                                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: 'white',
                                      fontSize: '1.2rem',
                                      fontWeight: 'bold'
                                    }}
                                  >
                                    +{vehicle.additional_images.length - 4}
                                  </Box>
                                )}
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                        {vehicle.additional_images && vehicle.additional_images.length > 4 && (
                          <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            sx={{ mt: 1, display: 'block', textAlign: 'center' }}
                          >
                            Cliquez pour voir toutes les images ({vehicle.additional_images.length + (vehicle.primary_image ? 1 : 0)} au total)
                          </Typography>
                        )}
                      </Grid>
                    )}
                  </Grid>
                  
                  {/* View All Images Button */}
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Button
                      variant="outlined"
                      startIcon={<PhotoCameraIcon />}
                      onClick={() => onShowImageGallery?.(vehicle)}
                      sx={{ mt: 1 }}
                    >
                      Voir toutes les images
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

          <Box display="flex" gap={2} flexDirection={{ xs: 'column', md: 'row' }}>
            <Card sx={{ flex: 1 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  <CarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Informations générales
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {FRENCH_LABELS.marque}
                    </Typography>
                    <Typography variant="body1">{vehicle.marque}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {FRENCH_LABELS.modele}
                    </Typography>
                    <Typography variant="body1">{vehicle.modele}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Année
                    </Typography>
                    <Typography variant="body1">{vehicle.year}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Kilométrage
                    </Typography>
                    <Typography variant="body1">{vehicle.mileage.toLocaleString()} km</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {FRENCH_LABELS.status}
                    </Typography>
                    <Chip 
                      label={statusOption?.label || vehicle.status}
                      sx={{ 
                        backgroundColor: statusOption?.color || '#grey',
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <Card sx={{ flex: 1 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  <MaintenanceIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Maintenance & Dates
                </Typography>
                <Stack spacing={2}>
                  {vehicle.date_achat && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {FRENCH_LABELS.dateAchat}
                      </Typography>
                      <Typography variant="body1">
                        {new Date(vehicle.date_achat).toLocaleDateString('fr-FR')}
                      </Typography>
                    </Box>
                  )}
                  {vehicle.date_derniere_intervention && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {FRENCH_LABELS.dateDerniereIntervention}
                      </Typography>
                      <Typography variant="body1">
                        {new Date(vehicle.date_derniere_intervention).toLocaleDateString('fr-FR')}
                      </Typography>
                    </Box>
                  )}
                  {vehicle.prochaine_revision && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {FRENCH_LABELS.prochaineRevision}
                      </Typography>
                      <Typography variant="body1">
                        {new Date(vehicle.prochaine_revision).toLocaleDateString('fr-FR')}
                      </Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Intervalle de service
                    </Typography>
                    <Typography variant="body1">{vehicle.service_interval_km.toLocaleString()} km</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Box>

          {(vehicle.fuel_type || vehicle.transmission_type || vehicle.color) && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Spécifications techniques
                </Typography>
                <Box display="flex" gap={3} flexWrap="wrap">
                  {vehicle.fuel_type && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {FRENCH_LABELS.fuelType}
                      </Typography>
                      <Typography variant="body1">{vehicle.fuel_type}</Typography>
                    </Box>
                  )}
                  {vehicle.transmission_type && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {FRENCH_LABELS.transmissionType}
                      </Typography>
                      <Typography variant="body1">{vehicle.transmission_type}</Typography>
                    </Box>
                  )}
                  {vehicle.color && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {FRENCH_LABELS.color}
                      </Typography>
                      <Typography variant="body1">{vehicle.color}</Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          )}

          {(vehicle.equipe_assignee || vehicle.parking_spot) && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  <ParkingIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Affectation
                </Typography>
                <Box display="flex" gap={3} flexWrap="wrap">
                  {vehicle.equipe_assignee && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {FRENCH_LABELS.equipe}
                      </Typography>
                      <Typography variant="body1">{vehicle.equipe_assignee}</Typography>
                    </Box>
                  )}
                  {vehicle.parking_spot && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Place de parking
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1">
                          {vehicle.parking_spot.spot_number} - {vehicle.parking_spot.location}
                        </Typography>
                        <Tooltip title="Réassigner la place">
                          <IconButton 
                            size="small" 
                            onClick={() => {
                              if (onShowParkingLocation) {
                                onShowParkingLocation(vehicle);
                              }
                            }}
                            color="primary"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          )}

          {(vehicle.primary_image_url || vehicle.additional_images_urls?.length) && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  <PhotoCameraIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Images du véhicule
                </Typography>
                
                {vehicle.primary_image_url && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Image principale
                    </Typography>
                    <Avatar
                      src={vehicle.primary_image_url}
                      sx={{ width: 120, height: 120, mb: 2 }}
                      variant="rounded"
                    />
                  </Box>
                )}
                
                {vehicle.additional_images_urls?.length && vehicle.additional_images_urls.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Images supplémentaires
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {vehicle.additional_images_urls.map((url, index) => (
                        <Avatar
                          key={index}
                          src={url}
                          sx={{ width: 80, height: 80 }}
                          variant="rounded"
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Maintenance Records */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                <MaintenanceIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Historique de maintenance et interventions
              </Typography>
              {loadingMaintenance ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : maintenanceRecords.length > 0 ? (
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                  {maintenanceRecords.map((record) => (
                    <Card 
                      key={record.id} 
                      sx={{ 
                        mb: 2, 
                        border: '1px solid #e0e0e0',
                        backgroundColor: record.is_calculated ? '#f8f9fa' : 'white'
                      }}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {record.description}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            {record.is_calculated && (
                              <Chip 
                                label="Calculé"
                                size="small"
                                color="info"
                                variant="outlined"
                              />
                            )}
                            <Chip 
                              label={record.status}
                              size="small"
                              color={
                                record.status === 'completed' ? 'success' :
                                record.status === 'in_progress' ? 'warning' :
                                record.status === 'scheduled' ? 'info' : 'default'
                              }
                            />
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Type
                            </Typography>
                            <Typography variant="body2">
                              {record.maintenance_type}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Date de début
                            </Typography>
                            <Typography variant="body2">
                              {new Date(record.start_date).toLocaleDateString('fr-FR')}
                            </Typography>
                          </Box>
                          {record.completion_date && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Date de fin
                              </Typography>
                              <Typography variant="body2">
                                {new Date(record.completion_date).toLocaleDateString('fr-FR')}
                              </Typography>
                            </Box>
                          )}
                          {record.cost && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Coût
                              </Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {record.cost.toLocaleString('fr-FR')} €
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        {record.service_provider && (
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              Prestataire
                            </Typography>
                            <Typography variant="body2">
                              {record.service_provider}
                            </Typography>
                          </Box>
                        )}
                        {record.notes && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Notes
                            </Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {record.notes}
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              ) : (
                <Alert severity="info">
                  Aucun enregistrement de maintenance trouvé pour ce véhicule.
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* États des Lieux Section */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                <DescriptionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                États des Lieux
              </Typography>
              {loadingAssessments ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : assessments.length > 0 ? (
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  <Stack spacing={2}>
                    {assessments.map((assessment) => (
                      <Card 
                        key={assessment.id} 
                        sx={{ 
                          border: '1px solid #e0e0e0',
                          '&:hover': {
                            boxShadow: 2,
                            borderColor: 'primary.main'
                          }
                        }}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Box>
                              <Typography variant="subtitle1" fontWeight="bold">
                                État des lieux #{assessment.id}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(assessment.assessment_date).toLocaleDateString('fr-FR', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <Chip 
                                label={assessment.assessment_type === 'depart' ? 'Départ' : 'Retour'}
                                size="small"
                                color={assessment.assessment_type === 'depart' ? 'primary' : 'secondary'}
                                sx={{ textTransform: 'capitalize' }}
                              />
                              <Chip 
                                label={assessment.status === 'good' ? 'Bon' : 
                                      assessment.status === 'fair' ? 'Correct' :
                                      assessment.status === 'poor' ? 'Médiocre' : 'Critique'}
                                size="small"
                                color={
                                  assessment.status === 'good' ? 'success' :
                                  assessment.status === 'fair' ? 'info' :
                                  assessment.status === 'poor' ? 'warning' : 'error'
                                }
                              />
                            </Box>
                          </Box>
                          
                          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                            {assessment.sub_client_display_name && (
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Client
                                </Typography>
                                <Typography variant="body2">
                                  {assessment.sub_client_display_name}
                                  {assessment.sub_client_client_name && ` (${assessment.sub_client_client_name})`}
                                </Typography>
                              </Box>
                            )}
                            {assessment.photos && assessment.photos.length > 0 && (
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Photos
                                </Typography>
                                <Typography variant="body2">
                                  {assessment.photos.length} photo{assessment.photos.length > 1 ? 's' : ''}
                                </Typography>
                              </Box>
                            )}
                            {assessment.notes && assessment.notes.length > 0 && (
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Notes
                                </Typography>
                                <Typography variant="body2">
                                  {assessment.notes.length} note{assessment.notes.length > 1 ? 's' : ''}
                                </Typography>
                              </Box>
                            )}
                          </Box>

                          {(assessment.general_notes || assessment.damage_notes || assessment.alert_notes) && (
                            <Box sx={{ mt: 1 }}>
                              {assessment.general_notes && (
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                  <strong>Notes générales:</strong> {assessment.general_notes}
                                </Typography>
                              )}
                              {assessment.damage_notes && (
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                  <strong>Notes dommages:</strong> {assessment.damage_notes}
                                </Typography>
                              )}
                              {assessment.alert_notes && (
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                  <strong>Notes alertes:</strong> {assessment.alert_notes}
                                </Typography>
                              )}
                            </Box>
                          )}

                          {(assessment.created_by_user || assessment.created_by_name) && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              Créé par: {assessment.created_by_name || assessment.created_by_user?.name || assessment.created_by_user?.email || `Utilisateur #${assessment.created_by}`}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                </Box>
              ) : (
                <Alert severity="info">
                  Aucun état des lieux trouvé pour ce véhicule.
                </Alert>
              )}
            </CardContent>
          </Card>

        </Stack>

        {/* Notes Section */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
              Notes
            </Typography>
            
            {/* Notes List */}
            {loadingNotes ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : notes.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                Aucune note pour ce véhicule
              </Typography>
            ) : (
              <Stack spacing={2}>
                {notes.map((note) => (
                  <Box
                    key={note.id}
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      backgroundColor: 'background.paper',
                      position: 'relative'
                    }}
                  >
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      {note.content}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(note.created_at).toLocaleString('fr-FR')}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
};

// Parking Location Popup Component
const ParkingLocationDialog: React.FC<{
  open: boolean;
  vehicle: Vehicle | null;
  onClose: () => void;
  onReassign?: (vehicleId: string, newSpotId: string) => void;
}> = ({ open, vehicle, onClose, onReassign }) => {
  const [parkingSpots, setParkingSpots] = useState<ParkingSpot[]>([]);
  const [currentLayout, setCurrentLayout] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  const [showReassignMode, setShowReassignMode] = useState(false);

  useEffect(() => {
    if (open && vehicle) {
      loadParkingData();
    }
  }, [open, vehicle]);

  const loadParkingData = async () => {
    setLoading(true);
    try {
      // Load parking spots
      const spotsResponse = await api.get('/parking-spots');
      setParkingSpots(spotsResponse.data.data || []);

      // Load ALL parking layouts (not just active ones) to find the correct plan
      const layoutsResponse = await api.get('/parking-layouts');
      const layouts = layoutsResponse.data.data || [];
      
      // Find the layout that contains the vehicle's parking spot
      let targetLayout = null;
      if (vehicle?.place_parking_id) {
        // Find which layout contains this parking spot
        for (const layout of layouts) {
          const layoutResponse = await api.get(`/parking-layouts/${layout.id}`);
          const fullLayoutData = layoutResponse.data;
          const layoutData = fullLayoutData.data || fullLayoutData;
          
          const settings = layoutData.settings || {};
          const visualSpots = settings.visual_spots || [];
          
          // Check if this layout contains the vehicle's parking spot
          const hasVehicleSpot = visualSpots.some((visualSpot: any) => 
            visualSpot.assigned_spot_id && 
            vehicle.place_parking_id &&
            visualSpot.assigned_spot_id.toString() === vehicle.place_parking_id.toString()
          );
          
          if (hasVehicleSpot) {
            targetLayout = layout;
            break;
          }
        }
      }
      
      // If no specific layout found, use the first active layout
      if (!targetLayout) {
        targetLayout = layouts.find((layout: any) => layout.is_active);
      }
      
      if (targetLayout) {
        const layoutResponse = await api.get(`/parking-layouts/${targetLayout.id}`);
        const fullLayoutData = layoutResponse.data;
        const layoutData = fullLayoutData.data || fullLayoutData;
        
        const settings = layoutData.settings || {};
        const visualSpots = settings.visual_spots || [];
        const visualElements = settings.visual_elements || [];
        
        const layoutWithVisualData = {
          ...targetLayout,
          spots: visualSpots,
          elements: visualElements,
          canvas: {
            width: layoutData.canvas_width || 1400,
            height: layoutData.canvas_height || 900,
            background_color: layoutData.background_color || '#ffffff',
          }
        };
        
        setCurrentLayout(layoutWithVisualData);
      }
    } catch (error) {
      console.error('Error loading parking data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSpotColor = (visualSpot: any) => {
    if (!visualSpot.assigned_spot_id) return '#e0e0e0';
    
    const realSpot = parkingSpots.find(spot => spot.id.toString() === visualSpot.assigned_spot_id.toString());
    if (!realSpot) return '#e0e0e0';
    
    // Check if this is the vehicle's current parking spot
    const isVehicleSpot = vehicle?.place_parking_id && 
      realSpot.id.toString() === vehicle.place_parking_id.toString();
    
    if (isVehicleSpot) {
      return '#9c27b0'; // Purple highlight for the selected vehicle's spot
    }
    
    if (realSpot.is_occupied) {
      return '#f44336'; // Red for occupied
    }
    
    // Return normal color based on spot type
    const SPOT_TYPE_COLORS = {
      regular: '#2196f3',
      maintenance: '#ff9800',
      washing: '#9c27b0',
      premium: '#4caf50',
    };
    
    return SPOT_TYPE_COLORS[realSpot.spot_type as keyof typeof SPOT_TYPE_COLORS] || '#e0e0e0';
  };

  const getSpotText = (visualSpot: any) => {
    if (!visualSpot.assigned_spot_id) return '';
    
    const realSpot = parkingSpots.find(spot => spot.id.toString() === visualSpot.assigned_spot_id.toString());
    if (!realSpot) return 'N/A';
    
    // Check if this is the vehicle's current parking spot
    const isVehicleSpot = vehicle?.place_parking_id && 
      realSpot.id.toString() === vehicle.place_parking_id.toString();
    
    if (isVehicleSpot) {
      return `★ ${vehicle?.registration_number}`; // Add star indicator for the selected vehicle
    }
    
    if (realSpot.is_occupied && realSpot.vehicle) {
      return realSpot.vehicle.registration_number;
    }
    
    return realSpot.spot_number || 'N/A';
  };

  const getVehicleSpot = () => {
    if (!vehicle?.place_parking_id) return null;
    return parkingSpots.find(spot => spot.id === vehicle.place_parking_id);
  };

  const vehicleSpot = getVehicleSpot();

  const handleSpotClick = (spotData: any) => {
    if (!showReassignMode || !vehicle) return;
    
    // Find the real parking spot
    const realSpot = parkingSpots.find(spot => spot.id.toString() === spotData.assigned_spot_id?.toString());
    if (!realSpot) return;
    
    // Don't allow reassigning to the same spot
    if (realSpot.id === vehicle.place_parking_id) return;
    
    // Don't allow reassigning to an occupied spot
    if (realSpot.is_occupied) return;
    
    // Reassign the vehicle
    handleReassign(realSpot.id.toString());
  };

  const handleReassign = async (newSpotId: string) => {
    if (!vehicle || !onReassign) return;
    
    setReassigning(true);
    try {
      await onReassign(vehicle.id.toString(), newSpotId);
      setShowReassignMode(false);
      // Reload parking data to reflect changes
      await loadParkingData();
    } catch (error) {
      console.error('Error reassigning vehicle:', error);
    } finally {
      setReassigning(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocationIcon color="primary" />
          <Box>
            <Typography variant="h6" component="span">
          Emplacement du véhicule - {vehicle?.registration_number}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
              Le véhicule sélectionné est mis en évidence en violet sur le plan
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3} sx={{ mt: 2 }}>
            {/* Vehicle Parking Info */}
            {vehicleSpot ? (
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" color="primary">
                      <ParkingIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Place de parking assignée
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {!showReassignMode ? (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setShowReassignMode(true)}
                          disabled={reassigning}
                        >
                          Réassigner
                        </Button>
                      ) : (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setShowReassignMode(false)}
                          disabled={reassigning}
                        >
                          Annuler
                        </Button>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Numéro de place
                      </Typography>
                      <Typography variant="h6" color="primary">
                        {vehicleSpot.spot_number}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Emplacement
                      </Typography>
                      <Typography variant="body1">
                        {vehicleSpot.location}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Type de place
                      </Typography>
                      <Chip 
                        label={vehicleSpot.spot_type}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Statut
                      </Typography>
                      <Chip 
                        label={vehicleSpot.is_occupied ? 'Occupée' : 'Disponible'}
                        size="small"
                        color={vehicleSpot.is_occupied ? 'error' : 'success'}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ) : (
              <Alert severity="info">
                Ce véhicule n'est pas assigné à une place de parking.
              </Alert>
            )}

            {/* Parking Layout Map */}
            {currentLayout && (
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" color="primary">
                      Plan du parking
                    </Typography>
                    {showReassignMode && (
                      <Alert severity="info" sx={{ py: 0.5 }}>
                        Mode réassignation: Cliquez sur une place disponible pour réassigner le véhicule
                      </Alert>
                    )}
                  </Box>
                  
                  {/* Color Legend */}
                  <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                      Légende des couleurs :
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 16, height: 16, bgcolor: '#9c27b0', borderRadius: 0.5 }} />
                        <Typography variant="body2">Véhicule sélectionné</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 16, height: 16, bgcolor: '#f44336', borderRadius: 0.5 }} />
                        <Typography variant="body2">Occupé</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 16, height: 16, bgcolor: '#2196f3', borderRadius: 0.5 }} />
                        <Typography variant="body2">Disponible</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 16, height: 16, bgcolor: '#e0e0e0', borderRadius: 0.5 }} />
                        <Typography variant="body2">Sans liaison</Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ height: 500, position: 'relative' }}>
                    <CurrentLayoutDisplay 
                      layout={currentLayout} 
                      parkingSpots={parkingSpots}
                      onSpotClick={showReassignMode ? handleSpotClick : undefined}
                      getSpotColor={getSpotColor}
                      getSpotText={getSpotText}
                    />
                    {reassigning && (
                      <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                      }}>
                        <CircularProgress />
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            )}

            {!currentLayout && !loading && (
              <Alert severity="warning">
                Aucun plan de parking actuel disponible.
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
};

// Vehicle Image Gallery Modal Component
const VehicleImageGallery: React.FC<{
  open: boolean;
  vehicle: Vehicle | null;
  onClose: () => void;
}> = ({ open, vehicle, onClose }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    if (open) {
      setCurrentImageIndex(0);
    }
  }, [open]);

  if (!vehicle) return null;

  const allImages = [];
  if (vehicle.primary_image_url) {
    allImages.push({ url: vehicle.primary_image_url, type: 'primary' });
  }
  if (vehicle.additional_images_urls) {
    vehicle.additional_images_urls.forEach(url => {
      allImages.push({ url, type: 'additional' });
    });
  }

  const handlePrevious = () => {
    setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : allImages.length - 1));
  };

  const handleNext = () => {
    setCurrentImageIndex((prev) => (prev < allImages.length - 1 ? prev + 1 : 0));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') handlePrevious();
    if (e.key === 'ArrowRight') handleNext();
    if (e.key === 'Escape') onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          color: 'white',
        }
      }}
      onKeyDown={handleKeyDown}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <Typography variant="h6">
          Images de {vehicle.marque} {vehicle.modele} ({vehicle.registration_number})
        </Typography>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ 
        p: 0, 
        position: 'relative',
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {allImages.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            height: '100%',
            color: 'rgba(255, 255, 255, 0.7)'
          }}>
            <PhotoCameraIcon sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h6">Aucune image disponible</Typography>
            <Typography variant="body2">Ce véhicule n'a pas d'images</Typography>
          </Box>
        ) : (
          <>
            {/* Navigation Arrows */}
            {allImages.length > 1 && (
              <>
                <IconButton
                  onClick={handlePrevious}
                  sx={{
                    position: 'absolute',
                    left: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    },
                    zIndex: 2
                  }}
                >
                  <NavigateBeforeIcon />
                </IconButton>
                
                <IconButton
                  onClick={handleNext}
                  sx={{
                    position: 'absolute',
                    right: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    },
                    zIndex: 2
                  }}
                >
                  <NavigateNextIcon />
                </IconButton>
              </>
            )}

            {/* Main Image */}
            <Box
              component="img"
              src={allImages[currentImageIndex]?.url}
              alt={`${vehicle.marque} ${vehicle.modele} - Image ${currentImageIndex + 1}`}
              sx={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
                borderRadius: 1,
              }}
            />

            {/* Image Counter */}
            {allImages.length > 1 && (
              <Box sx={{
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                px: 2,
                py: 1,
                borderRadius: 1,
                fontSize: '14px'
              }}>
                {currentImageIndex + 1} / {allImages.length}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      {/* Thumbnail Navigation */}
      {allImages.length > 1 && (
        <Box sx={{ 
          p: 2, 
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          gap: 1,
          overflowX: 'auto',
          justifyContent: 'center'
        }}>
          {allImages.map((image, index) => (
            <Box
              key={index}
              component="img"
              src={image.url}
              alt={`Thumbnail ${index + 1}`}
              onClick={() => setCurrentImageIndex(index)}
              sx={{
                width: 80,
                height: 60,
                objectFit: 'cover',
                borderRadius: 1,
                cursor: 'pointer',
                border: currentImageIndex === index ? '2px solid #1976d2' : '2px solid transparent',
                opacity: currentImageIndex === index ? 1 : 0.7,
                transition: 'all 0.2s ease',
                '&:hover': {
                  opacity: 1,
                  transform: 'scale(1.05)',
                }
              }}
            />
          ))}
        </Box>
      )}
    </Dialog>
  );
};

// Vehicle Settings Dialog Component
const VehicleSettingsDialog: React.FC<{
  open: boolean;
  onClose: () => void;
}> = ({ open, onClose }) => {
  const [settings, setSettings] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [newType, setNewType] = useState({ name: '', name_en: '', description: '' });
  const [newCategory, setNewCategory] = useState({ name: '', name_en: '', description: '', vehicle_type_id: '' });
  const [editingType, setEditingType] = useState<any>(null);
  const [editingCategory, setEditingCategory] = useState<any>(null);
  const [typeImageFile, setTypeImageFile] = useState<File | null>(null);
  const [typePreviewUrl, setTypePreviewUrl] = useState<string | null>(null);
  const [typeRemoveImage, setTypeRemoveImage] = useState(false);
  const [categoryImageFile, setCategoryImageFile] = useState<File | null>(null);
  const [categoryPreviewUrl, setCategoryPreviewUrl] = useState<string | null>(null);
  const [categoryRemoveImage, setCategoryRemoveImage] = useState(false);
  const [typeUpdateError, setTypeUpdateError] = useState<string | null>(null);

  useEffect(() => {
    if (typeImageFile) {
      const url = URL.createObjectURL(typeImageFile);
      setTypePreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setTypePreviewUrl(null);
    }
  }, [typeImageFile]);

  useEffect(() => {
    if (categoryImageFile) {
      const url = URL.createObjectURL(categoryImageFile);
      setCategoryPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setCategoryPreviewUrl(null);
    }
  }, [categoryImageFile]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await vehicleSettingsService.getSettings();
      setSettings(response.data.data);
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadSettings();
    }
  }, [open]);

  useEffect(() => {
    if (editingType) {
      setTypeImageFile(null);
      setTypePreviewUrl(null);
      setTypeRemoveImage(false);
      setTypeUpdateError(null);
    }
  }, [editingType]);

  useEffect(() => {
    if (editingCategory) {
      setCategoryImageFile(null);
      setCategoryPreviewUrl(null);
      setCategoryRemoveImage(false);
    }
  }, [editingCategory]);

  const handleCreateType = async () => {
    try {
      await vehicleSettingsService.createType(newType);
      setNewType({ name: '', name_en: '', description: '' });
      loadSettings();
    } catch (error) {
      console.error('Error creating type:', error);
    }
  };

  const handleCreateCategory = async () => {
    try {
      await vehicleSettingsService.createCategory({
        ...newCategory,
        vehicle_type_id: parseInt(newCategory.vehicle_type_id),
      });
      setNewCategory({ name: '', name_en: '', description: '', vehicle_type_id: '' });
      loadSettings();
    } catch (error) {
      console.error('Error creating category:', error);
    }
  };

  const handleUpdateType = async () => {
    try {
      if (typeImageFile || typeRemoveImage) {
        const formData = new FormData();
        formData.append('name', editingType.name ?? '');
        formData.append('name_en', editingType.name_en ?? '');
        formData.append('description', editingType.description ?? '');
        if (typeRemoveImage) formData.append('remove_image', '1');
        if (typeImageFile) formData.append('image', typeImageFile);
        await vehicleTypeService.updateWithImage(editingType.id, formData);
      } else {
        await vehicleSettingsService.updateType(editingType.id, editingType);
      }
      setEditingType(null);
      setTypeImageFile(null);
      setTypePreviewUrl(null);
      setTypeRemoveImage(false);
      setTypeUpdateError(null);
      loadSettings();
    } catch (error: any) {
      const data = error?.response?.data;
      const msg = data?.message || (data?.errors && typeof data.errors === 'object' ? Object.values(data.errors).flat().join(' ') : data?.errors) || error?.message || 'Erreur lors de la mise à jour du type';
      setTypeUpdateError(msg);
      console.error('Error updating type:', error?.response?.data ?? error);
    }
  };

  const handleUpdateCategory = async () => {
    try {
      if (categoryImageFile || categoryRemoveImage) {
        const formData = new FormData();
        formData.append('name', editingCategory.name ?? '');
        formData.append('name_en', editingCategory.name_en ?? '');
        formData.append('description', editingCategory.description ?? '');
        formData.append('vehicle_type_id', String(editingCategory.vehicle_type_id ?? ''));
        if (categoryRemoveImage) formData.append('remove_image', '1');
        if (categoryImageFile) formData.append('image', categoryImageFile);
        await vehicleSettingsService.updateCategoryWithImage(editingCategory.id, formData);
      } else {
        await vehicleSettingsService.updateCategory(editingCategory.id, editingCategory);
      }
      setEditingCategory(null);
      setCategoryImageFile(null);
      setCategoryPreviewUrl(null);
      setCategoryRemoveImage(false);
      loadSettings();
    } catch (error) {
      console.error('Error updating category:', error);
    }
  };

  const handleDeleteType = async (id: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce type ?')) {
      try {
        await vehicleSettingsService.deleteType(id);
        loadSettings();
      } catch (error) {
        console.error('Error deleting type:', error);
      }
    }
  };

  const handleDeleteCategory = async (id: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette catégorie ?')) {
      try {
        await vehicleSettingsService.deleteCategory(id);
        loadSettings();
      } catch (error) {
        console.error('Error deleting category:', error);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Paramètres des véhicules</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Stack direction="row">
                <Button
                  onClick={() => setActiveTab(0)}
                  sx={{ 
                    borderBottom: activeTab === 0 ? 2 : 0,
                    borderColor: 'primary.main',
                    color: activeTab === 0 ? 'primary.main' : 'text.secondary'
                  }}
                >
                  Types de véhicules
                </Button>
                <Button
                  onClick={() => setActiveTab(1)}
                  sx={{ 
                    borderBottom: activeTab === 1 ? 2 : 0,
                    borderColor: 'primary.main',
                    color: activeTab === 1 ? 'primary.main' : 'text.secondary'
                  }}
                >
                  Catégories de véhicules
                </Button>
              </Stack>
            </Box>

            {activeTab === 0 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>Types de véhicules</Typography>

                {/* Client Types */}
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Vos types de véhicules
                </Typography>
                <Stack spacing={1} sx={{ mb: 3 }}>
                  {settings?.types?.map((type: any) => (
                    <Box key={type.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ width: 48, height: 48, borderRadius: 1.5, overflow: 'hidden', border: '1px solid', borderColor: 'divider', flexShrink: 0, bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {type.image_url ? (
                          <Box component="img" src={type.image_url} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <CarIcon sx={{ fontSize: 28, color: 'grey.500' }} />
                        )}
                      </Box>
                      <Chip label={type.name} />
                      <IconButton size="small" onClick={() => setEditingType(type)} title="Modifier (incl. image)">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteType(type.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>

                {/* Add New Type */}
                <Card sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>Ajouter un nouveau type</Typography>
                  <Stack spacing={2}>
                    <TextField
                      label="Nom du type"
                      value={newType.name}
                      onChange={(e) => setNewType({ ...newType, name: e.target.value })}
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label="Nom en anglais (optionnel)"
                      value={newType.name_en}
                      onChange={(e) => setNewType({ ...newType, name_en: e.target.value })}
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label="Description (optionnel)"
                      value={newType.description}
                      onChange={(e) => setNewType({ ...newType, description: e.target.value })}
                      fullWidth
                      size="small"
                      multiline
                      rows={2}
                    />
                    <Button variant="contained" onClick={handleCreateType} disabled={!newType.name}>
                      Ajouter le type
                    </Button>
                  </Stack>
                </Card>
              </Box>
            )}

            {activeTab === 1 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>Catégories de véhicules</Typography>

                {/* Categories grouped by type */}
                {settings?.types?.map((type: any) => {
                  const typeCategories = settings?.categories?.filter((cat: any) => cat.vehicle_type_id === type.id) || [];
                  if (typeCategories.length === 0) return null;
                  
                  return (
                    <Box key={type.id} sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                        {type.name}
                      </Typography>
                      <Stack spacing={1} sx={{ mb: 2 }}>
                        {typeCategories.map((category: any) => (
                          <Box key={category.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ width: 48, height: 48, borderRadius: 1.5, overflow: 'hidden', border: '1px solid', borderColor: 'divider', flexShrink: 0, bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {category.image_url ? (
                                <Box component="img" src={category.image_url} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              ) : (
                                <CarIcon sx={{ fontSize: 28, color: 'grey.500' }} />
                              )}
                            </Box>
                            <Chip label={category.name} />
                            <IconButton size="small" onClick={() => setEditingCategory(category)} title="Modifier (incl. image)">
                              <EditIcon />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDeleteCategory(category.id)}>
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  );
                })}

                {/* Categories without type */}
                {settings?.categories?.filter((cat: any) => !cat.vehicle_type_id)?.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                      Catégories sans type
                    </Typography>
                    <Stack spacing={1} sx={{ mb: 2 }}>
                      {settings?.categories?.filter((cat: any) => !cat.vehicle_type_id).map((category: any) => (
                        <Box key={category.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ width: 48, height: 48, borderRadius: 1.5, overflow: 'hidden', border: '1px solid', borderColor: 'divider', flexShrink: 0, bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {category.image_url ? (
                              <Box component="img" src={category.image_url} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <CarIcon sx={{ fontSize: 28, color: 'grey.500' }} />
                            )}
                          </Box>
                          <Chip label={category.name} />
                          <IconButton size="small" onClick={() => setEditingCategory(category)} title="Modifier (incl. image)">
                            <EditIcon />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDeleteCategory(category.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}

                {/* Add New Category */}
                <Card sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>Ajouter une nouvelle catégorie</Typography>
                  <Stack spacing={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Type de véhicule *</InputLabel>
                      <Select
                        value={newCategory.vehicle_type_id}
                        onChange={(e) => setNewCategory({ ...newCategory, vehicle_type_id: e.target.value })}
                        label="Type de véhicule *"
                      >
                        {settings?.types?.map((type: any) => (
                          <MenuItem key={type.id} value={type.id.toString()}>
                            {type.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      label="Nom de la catégorie"
                      value={newCategory.name}
                      onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label="Nom en anglais (optionnel)"
                      value={newCategory.name_en}
                      onChange={(e) => setNewCategory({ ...newCategory, name_en: e.target.value })}
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label="Description (optionnel)"
                      value={newCategory.description}
                      onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                      fullWidth
                      size="small"
                      multiline
                      rows={2}
                    />
                    <Button 
                      variant="contained" 
                      onClick={handleCreateCategory} 
                      disabled={!newCategory.name || !newCategory.vehicle_type_id}
                    >
                      Ajouter la catégorie
                    </Button>
                  </Stack>
                </Card>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      {/* Edit Category Dialog */}
      <Dialog open={!!editingCategory} onClose={() => { setEditingCategory(null); setCategoryImageFile(null); setCategoryPreviewUrl(null); setCategoryRemoveImage(false); }} maxWidth="sm" fullWidth>
        <DialogTitle>Modifier la catégorie</DialogTitle>
        <DialogContent>
          {editingCategory && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Type de véhicule *</InputLabel>
                <Select
                  value={editingCategory?.vehicle_type_id?.toString() || ''}
                  onChange={(e) => setEditingCategory({ ...editingCategory, vehicle_type_id: parseInt(e.target.value) })}
                  label="Type de véhicule *"
                >
                  {settings?.types?.map((type: any) => (
                    <MenuItem key={type.id} value={type.id.toString()}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Nom de la catégorie"
                value={editingCategory?.name || ''}
                onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                fullWidth
                size="small"
              />
              <TextField
                label="Nom en anglais (optionnel)"
                value={editingCategory?.name_en || ''}
                onChange={(e) => setEditingCategory({ ...editingCategory, name_en: e.target.value })}
                fullWidth
                size="small"
              />
              <TextField
                label="Description (optionnel)"
                value={editingCategory?.description || ''}
                onChange={(e) => setEditingCategory({ ...editingCategory, description: e.target.value })}
                fullWidth
                size="small"
                multiline
                rows={2}
              />
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Image de la catégorie</Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', flexShrink: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1, py: 0.5, bgcolor: 'action.hover' }}>Aperçu</Typography>
                    {categoryPreviewUrl || (editingCategory.image_url && !categoryRemoveImage) ? (
                      <Box
                        component="img"
                        src={categoryPreviewUrl || editingCategory.image_url}
                        alt="Preview"
                        sx={{ width: 120, height: 120, display: 'block', objectFit: 'cover' }}
                      />
                    ) : (
                      <Box sx={{ width: 120, height: 120, bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CarIcon sx={{ fontSize: 48, color: 'grey.400' }} />
                      </Box>
                    )}
                  </Box>
                  <Stack spacing={1}>
                    <Button size="small" component="label" startIcon={<CloudUploadIcon />}>
                      {categoryPreviewUrl || (editingCategory.image_url && !categoryRemoveImage) ? 'Changer' : 'Ajouter une image'}
                      <input type="file" hidden accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; setCategoryImageFile(f ?? null); setCategoryRemoveImage(false); e.target.value = ''; }} />
                    </Button>
                    {(categoryPreviewUrl || (editingCategory.image_url && !categoryRemoveImage)) && (
                      <Button size="small" color="secondary" onClick={() => { setCategoryRemoveImage(true); setCategoryImageFile(null); setCategoryPreviewUrl(null); }}>
                        Supprimer l&apos;image
                      </Button>
                    )}
                  </Stack>
                </Box>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditingCategory(null); setCategoryImageFile(null); setCategoryPreviewUrl(null); setCategoryRemoveImage(false); }}>Annuler</Button>
          <Button 
            onClick={handleUpdateCategory} 
            variant="contained"
            disabled={!editingCategory?.name || !editingCategory?.vehicle_type_id}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Type Dialog (with image upload) */}
      <Dialog open={!!editingType} onClose={() => { setEditingType(null); setTypeImageFile(null); setTypePreviewUrl(null); setTypeRemoveImage(false); setTypeUpdateError(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Modifier le type de véhicule</DialogTitle>
        <DialogContent>
          {editingType && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {typeUpdateError && (
                <Alert severity="error" onClose={() => setTypeUpdateError(null)}>{typeUpdateError}</Alert>
              )}
              <TextField
                label="Nom du type"
                value={editingType.name || ''}
                onChange={(e) => setEditingType({ ...editingType, name: e.target.value })}
                fullWidth
                size="small"
              />
              <TextField
                label="Nom en anglais (optionnel)"
                value={editingType.name_en || ''}
                onChange={(e) => setEditingType({ ...editingType, name_en: e.target.value })}
                fullWidth
                size="small"
              />
              <TextField
                label="Description (optionnel)"
                value={editingType.description || ''}
                onChange={(e) => setEditingType({ ...editingType, description: e.target.value })}
                fullWidth
                size="small"
                multiline
                rows={2}
              />
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Image du type</Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', flexShrink: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1, py: 0.5, bgcolor: 'action.hover' }}>Aperçu</Typography>
                    {typePreviewUrl || (editingType.image_url && !typeRemoveImage) ? (
                      <Box
                        component="img"
                        src={typePreviewUrl || editingType.image_url}
                        alt="Preview"
                        sx={{ width: 120, height: 120, display: 'block', objectFit: 'cover' }}
                      />
                    ) : (
                      <Box sx={{ width: 120, height: 120, bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CarIcon sx={{ fontSize: 48, color: 'grey.400' }} />
                      </Box>
                    )}
                  </Box>
                  <Stack spacing={1}>
                    <Button size="small" component="label" startIcon={<CloudUploadIcon />}>
                      {typePreviewUrl || (editingType.image_url && !typeRemoveImage) ? 'Changer' : 'Ajouter une image'}
                      <input type="file" hidden accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; setTypeImageFile(f ?? null); setTypeRemoveImage(false); e.target.value = ''; }} />
                    </Button>
                    {(typePreviewUrl || (editingType.image_url && !typeRemoveImage)) && (
                      <Button size="small" color="secondary" onClick={() => { setTypeRemoveImage(true); setTypeImageFile(null); setTypePreviewUrl(null); }}>
                        Supprimer l&apos;image
                      </Button>
                    )}
                  </Stack>
                </Box>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditingType(null); setTypeImageFile(null); setTypePreviewUrl(null); setTypeRemoveImage(false); setTypeUpdateError(null); }}>Annuler</Button>
          <Button onClick={handleUpdateType} variant="contained" disabled={!editingType?.name}>
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

/** Table thumbnail: stable placeholder on missing/broken image (no innerHTML). */
const VehicleListThumbnail: React.FC<{
  vehicle: Vehicle;
  onOpenGallery: () => void;
}> = ({ vehicle, onOpenGallery }) => {
  const [imgError, setImgError] = useState(false);
  const url = vehicle.primary_image_url;
  const showImage = Boolean(url && !imgError);

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={`Galerie photos, ${vehicle.marque} ${vehicle.modele}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpenGallery();
        }
      }}
      onClick={onOpenGallery}
      sx={{
        width: 100,
        height: 60,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        position: 'relative',
        flexShrink: 0,
        '&:hover': { '& .zoom-overlay': { opacity: 1 } },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 2,
        },
      }}
    >
      {showImage ? (
        <>
          <Box
            component="img"
            src={url}
            alt={`${vehicle.marque} ${vehicle.modele}`}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
            onError={() => setImgError(true)}
          />
          <Box
            className="zoom-overlay"
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 1,
              opacity: 0,
              transition: 'opacity 0.2s ease',
            }}
          >
            <ZoomInIcon sx={{ color: 'white', fontSize: 24 }} />
          </Box>
        </>
      ) : (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.100',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': { bgcolor: 'grey.200' },
          }}
          aria-hidden
        >
          <CarIcon sx={{ fontSize: 28, color: 'grey.500' }} />
        </Box>
      )}
    </Box>
  );
};

const Vehicles: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector((state: any) => state.auth.user);
  const vehicles = useAppSelector(selectVehiclesList);
  const loading = useAppSelector(selectVehiclesLoading);
  const error = useAppSelector(selectVehiclesError);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [marqueFilter, setMarqueFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [showParkingLocation, setShowParkingLocation] = useState(false);
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [showVehicleSettings, setShowVehicleSettings] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchVehicles({}));
  }, [dispatch]);

  // Get unique marques for filter
  const uniqueMarques = Array.from(new Set(vehicles.map(v => v.marque))).sort();

  // Filter vehicles
  const filteredVehicles = vehicles.filter(vehicle => {
    const matchesSearch = 
      vehicle.registration_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.marque.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.modele.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = !statusFilter || vehicle.status === statusFilter;
    const matchesMarque = !marqueFilter || vehicle.marque === marqueFilter;
    
    return matchesSearch && matchesStatus && matchesMarque;
  });

  // Paginate filtered vehicles
  const paginatedVehicles = filteredVehicles.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleFormSubmit = async (data: VehicleFormData, maintenanceRecords: any[], originalMaintenanceRecords: any[] = []) => {
    // Debug: Log the form data being submitted
    console.log('Form submission data:', {
      hasPrimaryImage: !!data.primary_image,
      primaryImageType: data.primary_image?.type,
      primaryImageConstructor: data.primary_image?.constructor?.name,
      hasAdditionalImages: !!(data.additional_images && data.additional_images.length > 0),
      additionalImagesCount: data.additional_images?.length,
      additionalImagesTypes: data.additional_images?.map(img => img?.constructor?.name),
      removePrimaryImage: data.remove_primary_image,
      removeAdditionalImages: data.remove_additional_images,
      allKeys: Object.keys(data),
      fullData: data
    });
    
    console.log('Maintenance records to be created:', maintenanceRecords);
    
    setFormLoading(true);
    try {
      let vehicleId: number;
      
      if (selectedVehicle) {
        await dispatch(updateVehicle({ id: selectedVehicle.id, data })).unwrap();
        vehicleId = selectedVehicle.id;
      } else {
        const result = await dispatch(createVehicle(data)).unwrap();
        vehicleId = result.id;
      }

      // Handle maintenance records
      console.log('Processing maintenance records:', maintenanceRecords);
      console.log('Original maintenance records:', originalMaintenanceRecords);
      
      // First, handle deletions - find records that were in originalMaintenanceRecords but not in current maintenanceRecords
      const recordsToDelete = originalMaintenanceRecords.filter(originalRecord => 
        !maintenanceRecords.some(currentRecord => 
          currentRecord.id === originalRecord.id || 
          (originalRecord.id && currentRecord.id && originalRecord.id.toString() === currentRecord.id.toString())
        )
      );
      
      console.log('Records to delete:', recordsToDelete);
      
      // Delete removed records
      for (const recordToDelete of recordsToDelete) {
        if (recordToDelete.id && !recordToDelete.id.toString().startsWith('temp_') && !isNaN(Number(recordToDelete.id))) {
          try {
            console.log('Deleting maintenance record:', recordToDelete.id);
            await api.delete(`/maintenance/${recordToDelete.id}`);
            console.log('Maintenance record deleted successfully:', recordToDelete.id);
          } catch (deleteError) {
            console.error('Error deleting maintenance record:', deleteError);
            const errorMessage = deleteError instanceof Error ? deleteError.message : 'Erreur inconnue';
            alert(`Erreur lors de la suppression de la maintenance: ${errorMessage}`);
          }
        }
      }
      
      // Then handle updates and creations
      if (maintenanceRecords.length > 0) {
        for (const record of maintenanceRecords) {
          // Skip system-generated records (they're managed automatically)
          if (record.is_system_generated) {
            console.log('Skipping system-generated record:', record.id);
            continue;
          }
          
          if (record.maintenance_type && record.start_date) {
            // Skip records with invalid IDs (like 'calc_revision_overdue')
            if (record.id && !record.id.toString().startsWith('temp_') && isNaN(Number(record.id))) {
              console.log('Skipping record with invalid ID:', record.id);
              continue;
            }
            
            try {
              if (record.id && !record.id.toString().startsWith('temp_') && !isNaN(Number(record.id))) {
                // Update existing maintenance record (only if it's a real database ID)
                console.log('Updating maintenance record:', record);
                const response = await api.put(`/maintenance/${record.id}`, {
                  vehicle_id: vehicleId,
                  maintenance_type: record.maintenance_type,
                  description: record.description || `Maintenance ${record.maintenance_type}`,
                  start_date: record.start_date,
                  completion_date: record.completion_date || null,
                  cost: record.cost || 0,
                  service_provider: record.service_provider || null,
                  technician_id: null,
                  notes: record.notes || null,
                  status: record.status || 'scheduled',
                  client_id: user?.client_id || null
                });
                console.log('Maintenance record updated successfully:', response.data);
              } else {
                // Create new maintenance record
                console.log('Creating maintenance record:', record);
                const response = await api.post('/maintenance', {
                  vehicle_id: vehicleId,
                  maintenance_type: record.maintenance_type,
                  description: record.description || `Maintenance ${record.maintenance_type}`,
                  start_date: record.start_date,
                  completion_date: record.completion_date || null,
                  cost: record.cost || 0,
                  service_provider: record.service_provider || null,
                  technician_id: null,
                  notes: record.notes || null,
                  status: 'scheduled',
                  client_id: user?.client_id || null
                });
                console.log('Maintenance record created successfully:', response.data);
              }
            } catch (maintenanceError) {
              console.error('Error processing maintenance record:', maintenanceError);
              const errorMessage = maintenanceError instanceof Error ? maintenanceError.message : 'Erreur inconnue';
              alert(`Erreur lors de la sauvegarde de la maintenance: ${errorMessage}`);
              // Continue with other records even if one fails
            }
          } else {
            console.log('Skipping maintenance record - missing required fields:', record);
          }
        }
      }

      setShowForm(false);
      setSelectedVehicle(null);
      dispatch(fetchVehicles({}));
      // Show success message
      alert(selectedVehicle ? 'Véhicule mis à jour avec succès!' : 'Véhicule créé avec succès!');
    } catch (error: any) {
      console.error('Error saving vehicle:', error);
      // Show error message
      alert(`Erreur: ${error.message || 'Une erreur est survenue lors de la sauvegarde du véhicule.'}`);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (vehicle: Vehicle) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer le véhicule ${vehicle.registration_number} ?`)) {
      try {
        await dispatch(deleteVehicle(vehicle.id)).unwrap();
        dispatch(fetchVehicles({}));
      } catch (error) {
        console.error('Error deleting vehicle:', error);
      }
    }
  };

  const handleEdit = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setShowForm(true);
  };

  const handleView = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setShowDetail(true);
  };

  const handleShowParkingLocation = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setShowParkingLocation(true);
  };

  const handleReassignVehicle = async (vehicleId: string, newSpotId: string) => {
    setFormLoading(true);
    try {
      // First, release the vehicle from its current spot
      const vehicle = vehicles.find(v => v.id.toString() === vehicleId);
      if (vehicle?.place_parking_id) {
        await api.patch(`/parking-spots/${vehicle.place_parking_id}/release`);
      }
      
      // Then assign to the new spot
      await api.patch(`/parking-spots/${newSpotId}/assign`, { vehicle_id: vehicleId });
      
      // Refresh vehicles data
      await dispatch(fetchVehicles({}));
      
      setSuccess('Véhicule réassigné avec succès');
    } catch (error) {
      console.error('Error reassigning vehicle:', error);
      setErrorMessage('Erreur lors de la réassignation du véhicule');
    } finally {
      setFormLoading(false);
    }
  };

  const handleShowImageGallery = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setShowImageGallery(true);
  };

  const handleCreateNew = () => {
    setSelectedVehicle(null);
    setShowForm(true);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setMarqueFilter('');
    setPage(0);
  };

  if (loading && vehicles.length === 0) {
    return (
      <Box sx={{ 
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Container maxWidth="xl" sx={{ py: 4, flex: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
            <CircularProgress size={60} />
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        maxWidth: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflowX: 'hidden',
      }}
      aria-busy={loading && vehicles.length > 0}
    >
      {loading && vehicles.length > 0 && (
        <LinearProgress sx={{ position: 'sticky', top: 0, zIndex: 10 }} aria-label="Actualisation des véhicules" />
      )}
      <Container maxWidth="xl" sx={{ py: 4, flex: 1, maxWidth: '100%' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              Gestion de la flotte de véhicules
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filteredVehicles.length === 0
                ? 'Aucun véhicule trouvé'
                : filteredVehicles.length === 1
                  ? '1 véhicule trouvé'
                  : `${filteredVehicles.length} véhicules trouvés`}
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              startIcon={<SettingsIcon />}
              onClick={() => setShowVehicleSettings(true)}
              sx={{
                bgcolor: 'secondary.main',
                color: 'white',
                '&:hover': { bgcolor: 'secondary.dark' },
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Modifier type et catégorie
            </Button>
            <Tooltip title="Actualiser les données">
              <span>
              <IconButton 
                onClick={() => dispatch(fetchVehicles({}))} 
                disabled={loading}
                aria-label="Actualiser les données"
                sx={{ 
                  bgcolor: 'primary.main', 
                  color: 'white',
                  '&:hover': { bgcolor: 'primary.dark' },
                  '&:disabled': { bgcolor: 'grey.300' }
                }}
              >
                <RefreshIcon />
              </IconButton>
              </span>
            </Tooltip>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={handleCreateNew}
              sx={{
                borderRadius: 2,
                background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #45a049 0%, #3d8b40 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(76, 175, 80, 0.3)',
                },
                transition: 'all 0.3s ease-in-out',
              }}
            >
              Ajouter un véhicule
            </Button>
          </Stack>
        </Stack>
        
      {success && (
        <Alert 
          severity="success" 
            sx={{ mb: 3, borderRadius: 2 }} 
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}
      {errorMessage && (
        <Alert 
          severity="error" 
            sx={{ mb: 3, borderRadius: 2 }} 
          onClose={() => setErrorMessage(null)}
        >
          {errorMessage}
        </Alert>
      )}
      {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3, borderRadius: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => dispatch(fetchVehicles({}))}>
                Réessayer
              </Button>
            }
          >
          {error}
        </Alert>
      )}
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 4, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
            <Typography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
              <FilterIcon sx={{ mr: 1, color: 'primary.main' }} />
          Filtres et recherche
        </Typography>
            <Button 
              variant="outlined" 
              startIcon={<ClearIcon />}
              onClick={resetFilters}
              sx={{ borderRadius: 2 }}
            >
              Réinitialiser
            </Button>
          </Stack>
          
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            fullWidth
            label="Rechercher"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
            }}
            placeholder="Immatriculation, marque, modèle..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
          />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
          <FormControl fullWidth>
            <InputLabel>Statut</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
                  sx={{
                    borderRadius: 2,
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        zIndex: 9999,
                      },
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                  }}
            >
              <MenuItem value="">Tous les statuts</MenuItem>
              {STATUS_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: option.color,
                            mr: 1
                          }}
                        />
                  {option.label}
                      </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
          <FormControl fullWidth>
            <InputLabel>Marque</InputLabel>
            <Select
              value={marqueFilter}
              onChange={(e) => setMarqueFilter(e.target.value)}
                  sx={{
                    borderRadius: 2,
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        zIndex: 9999,
                      },
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                  }}
            >
              <MenuItem value="">Toutes les marques</MenuItem>
              {uniqueMarques.map(marque => (
                <MenuItem key={marque} value={marque}>
                  {marque}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Vehicle Table — single page scroll; horizontal scroll only inside table if needed */}
      <Card sx={{ 
        borderRadius: 2, 
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        maxWidth: '100%',
      }}>
        <TableContainer sx={{ overflowX: 'auto', width: '100%' }}>
          <Table sx={{ width: '100%', minWidth: 960, tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Image</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Véhicule</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Année</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Kilométrage</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Statut</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Maintenance</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Équipe</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Parking</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', py: 2 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedVehicles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <CarIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        Aucun véhicule trouvé
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {searchTerm || statusFilter || marqueFilter 
                          ? 'Aucun véhicule ne correspond à vos critères de recherche.'
                          : 'Commencez par ajouter des véhicules à votre flotte.'
                        }
                      </Typography>
                      {!searchTerm && !statusFilter && !marqueFilter && (
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={handleCreateNew}
                          sx={{
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #45a049 0%, #3d8b40 100%)',
                            }
                          }}
                        >
                          Ajouter le premier véhicule
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedVehicles.map((vehicle) => {
                const statusOption = STATUS_OPTIONS.find(s => s.value === vehicle.status);
                return (
                  <TableRow key={vehicle.id} hover>
                    <TableCell sx={{ width: 116, verticalAlign: 'middle' }}>
                      <VehicleListThumbnail
                        vehicle={vehicle}
                        onOpenGallery={() => handleShowImageGallery(vehicle)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ overflow: 'hidden' }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        {vehicle.registration_number}
                      </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {vehicle.marque} {vehicle.modele}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ overflow: 'hidden' }}>
                        <Typography variant="body2" fontWeight="medium" sx={{ 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap' 
                        }}>
                          {vehicle.vehicle_type?.name || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap' 
                        }}>
                          {vehicle.vehicle_category?.name || 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {vehicle.year}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {vehicle.mileage.toLocaleString()} km
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={statusOption?.label || vehicle.status}
                        sx={{ 
                          backgroundColor: statusOption?.color || '#666',
                          color: 'white',
                          fontWeight: 'medium',
                          fontSize: '0.75rem',
                        }}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220, verticalAlign: 'top' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                          maxHeight: 88,
                          overflowY: 'auto',
                          overflowX: 'hidden',
                          pr: 0.5,
                        }}
                      >
                        {vehicle.maintenance_records && vehicle.maintenance_records.length > 0 ? (
                          vehicle.maintenance_records.map((record, idx) => (
                            <Chip
                              key={idx}
                              label={`${record.maintenance_type} - ${new Date(record.start_date).toLocaleDateString('fr-FR')}`}
                              size="small"
                              color={
                                record.status === 'overdue' ? 'error' :
                                record.status === 'scheduled' ? 'info' :
                                record.status === 'in_progress' ? 'warning' : 'default'
                              }
                              variant="outlined"
                              sx={{ 
                                fontSize: '0.7rem',
                                height: '20px',
                                fontWeight: 500
                              }}
                            />
                          ))
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Aucune
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                        <Typography variant="body2" color="text.secondary">
                        {vehicle.equipe_assignee || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ overflow: 'hidden' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap' 
                        }}>
                          {vehicle.parking_spot ? `${vehicle.parking_spot.spot_number}` : 'N/A'}
                        </Typography>
                        {vehicle.parking_spot && (
                          <Typography variant="caption" color="text.secondary" sx={{ 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap' 
                          }}>
                            {vehicle.parking_spot.location}
                        </Typography>
                      )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Stack 
                        direction="row" 
                        spacing={0.5} 
                        justifyContent="center"
                        sx={{ 
                          opacity: 0.7,
                          transition: 'opacity 0.2s ease-in-out'
                        }}
                      >
                      <Tooltip title="Voir les détails">
                          <IconButton
                            size="small"
                            onClick={() => handleView(vehicle)}
                            aria-label={`Voir les détails du véhicule ${vehicle.registration_number}`}
                            sx={{ 
                              color: 'primary.main',
                              '&:hover': { 
                                bgcolor: 'primary.50',
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.2s ease-in-out'
                            }}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Voir l'emplacement parking">
                          <IconButton
                            size="small"
                            onClick={() => handleShowParkingLocation(vehicle)}
                            aria-label={`Parking pour ${vehicle.registration_number}`}
                            sx={{ 
                              color: 'info.main',
                              '&:hover': { 
                                bgcolor: 'info.50',
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.2s ease-in-out'
                            }}
                          >
                            <ParkingIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Modifier">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(vehicle)}
                            aria-label={`Modifier le véhicule ${vehicle.registration_number}`}
                            sx={{ 
                              color: 'warning.main',
                              '&:hover': { 
                                bgcolor: 'warning.50',
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.2s ease-in-out'
                            }}
                          >
                            <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(vehicle)}
                            aria-label={`Supprimer le véhicule ${vehicle.registration_number}`}
                            sx={{ 
                              color: 'error.main',
                              '&:hover': { 
                                bgcolor: 'error.50',
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.2s ease-in-out'
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredVehicles.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Lignes par page:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
          SelectProps={{
            MenuProps: {
              anchorOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
            },
          }}
          sx={{
            borderTop: '1px solid',
            borderColor: 'grey.200',
            '& .MuiTablePagination-toolbar': {
              px: 3,
            }
          }}
        />
      </Card>

      {/* Forms and Dialogs */}
      <VehicleForm
        open={showForm}
        vehicle={selectedVehicle}
        onClose={() => {
          setShowForm(false);
          setSelectedVehicle(null);
        }}
        onSubmit={handleFormSubmit}
        loading={formLoading}
        onShowParkingLocation={handleShowParkingLocation}
      />

      <VehicleDetail
        open={showDetail}
        vehicle={selectedVehicle}
        onClose={() => {
          setShowDetail(false);
          setSelectedVehicle(null);
        }}
        onShowParkingLocation={handleShowParkingLocation}
        onShowImageGallery={handleShowImageGallery}
      />

      <ParkingLocationDialog
        open={showParkingLocation}
        vehicle={selectedVehicle}
        onClose={() => {
          setShowParkingLocation(false);
          setSelectedVehicle(null);
        }}
        onReassign={handleReassignVehicle}
      />

      <VehicleImageGallery
        open={showImageGallery}
        vehicle={selectedVehicle}
        onClose={() => {
          setShowImageGallery(false);
          setSelectedVehicle(null);
        }}
      />

      <VehicleSettingsDialog
        open={showVehicleSettings}
        onClose={() => setShowVehicleSettings(false)}
      />

      </Container>
    </Box>
  );
};

export default Vehicles; 